import type { Annotation } from "@rowan/prisma";
import { useRef } from "react";
import type { SearchHit } from "../../../../api/src/algolia";
import type { GetBookmarksByPublicationOutput } from "../../../../api/src/types";
import type { ContentsItem } from "../contexts/WithPublication/PublicationContext";

export const scopeCSS = (css: string, prefix: string) => {
  return css.replace(/(^|\})\s*([^{]+)/g, (_, sep, selector) => {
    const scoped = selector
      .split(",")
      .map((s: string) => `${prefix} ${s.trim()}`)
      .join(", ");
    return `${sep} ${scoped}`;
  });
};

export const extractCSS = (raw: string): string => {
  return raw
    .replace(/<style[^>]*>/gi, "")
    .replace(/<\/style>/gi, "")
    .replace(/<!--/g, "")
    .replace(/-->/g, "")
    .trim();
};

export const extractChapterKey = (fullId: string): string | null => {
  const match = fullId.match(/^(.+?-ch:\d+)/);
  return match?.[1] ?? null;
};

export const extractSectionPath = (fullId: string): string | null => {
  const sectionPath = fullId.replace(
    /(?:-leaf:\d+|-display:\d+|-other:\d+)$/,
    "",
  );
  return sectionPath || null;
};

type TreeNode = {
  id: string;
  label?: string;
  type: string;
  children?: TreeNode[];
};

export const transformToTree = (
  nodes: TreeNode[],
  annotations?: Annotation[],
  bookmarks?: GetBookmarksByPublicationOutput,
): ContentsItem[] => {
  const isDescendantOrSelf = (descendantId: string, ancestorId: string) =>
    descendantId === ancestorId || descendantId.startsWith(`${ancestorId}-`);

  return nodes
    .filter((node) => node.label && node.id)
    .map((node) => ({
      ...node,
      label: node.label!,
      children: node.children
        ? transformToTree(node.children, annotations, bookmarks)
        : undefined,
      annotations: annotations?.filter((annotation) =>
        isDescendantOrSelf(annotation.range.startNodeId, node.id),
      ).length,
      bookmarks: bookmarks?.filter((bookmark) =>
        isDescendantOrSelf(bookmark.sectionId, node.id),
      ).length,
      bookmarkCollectionColours: bookmarks
        ?.filter((bookmark) => isDescendantOrSelf(bookmark.sectionId, node.id))
        .flatMap((bookmark) =>
          bookmark.collections.map((col) => col.collection.colour),
        ),
      // For items with undefined menuTypes that have children
      ownBookmarkCollectionColours: bookmarks
        ?.filter((bookmark) => bookmark.sectionId === node.id)
        .flatMap((bookmark) =>
          bookmark.collections.map((col) => col.collection.colour),
        ),
    }));
};

const getSection = (
  contentsItem: ContentsItem,
  id: string,
): ContentsItem | undefined => {
  if (contentsItem.id === id) {
    return contentsItem;
  }
  if (contentsItem.children) {
    for (const child of contentsItem.children) {
      const found: ContentsItem | undefined = getSection(child, id);
      if (found) {
        return found;
      }
    }
  }
};

export const getSectionFromContents = (
  contents: ContentsItem[],
  id: string,
) => {
  const chapterId = extractChapterKey(id);
  const chapter = contents.find((chapter) => chapter.id === chapterId);

  if (chapter) {
    return getSection(chapter, id);
  }
};

export const getParentIds = (id: string): string[] => {
  const parts = id.split("-");
  const base = parts[0];
  const hierarchy = parts.slice(1);

  const parents: string[] = [];
  for (let i = 1; i < hierarchy.length; i++) {
    const parentId = [base, ...hierarchy.slice(0, i)].join("-");
    parents.push(parentId);
  }

  return parents;
};

export const getHeadingParent = (
  id: string,
  contents: ContentsItem[],
): ContentsItem | undefined => {
  const section = getSectionFromContents(contents, id);
  if (section) {
    if (section.label) {
      return section;
    }
    const parents = getParentIds(section.id);
    for (let i = parents.length - 1; i >= 0; i--) {
      const parentId = parents[i];
      const parentSection = getSectionFromContents(contents, parentId);
      if (parentSection && parentSection.label) {
        return parentSection;
      }
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: unknown[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

// To be used when timeoutId is getting reinitialized due to component re-render
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const useDebounce = (func: Function, delay: number) => {
  const timeoutId = useRef<NodeJS.Timeout>(null);

  return (...args: unknown[]) => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => func(...args), delay);
  };
};

export const getParentElementWithId = (node: Node): string | undefined => {
  let current: Node | null = node;
  while (current) {
    if (
      current instanceof Element &&
      current.id &&
      !current.id.startsWith("footnote")
    ) {
      return current.id;
    }
    current = current.parentNode;
  }
  return undefined;
};

export const getAnnotationId = (annotation: Annotation): string => {
  return `${annotation.publicationId ?? annotation.articleSlug}_${annotation.id}`;
};

export const clusterMenus = (
  rawPositions: { id: string; x: number; y: number }[],
  rawPositionsSm: { id: string; x: number; y: number }[],
) => {
  const clustered: Record<string, { x: number; y: number }> = {};
  const threshold = 10;
  for (let i = 0; i < rawPositions.length; i++) {
    let offsetY = 0;
    let smOffsetY = 0;
    for (let j = 0; j < i; j++) {
      const a = rawPositions[i];
      const b = rawPositions[j];
      const c = rawPositionsSm[i];
      const d = rawPositionsSm[j];
      const closeX = Math.abs(a.x - b.x) < threshold;
      const closeY = Math.abs(a.y - b.y) < threshold;
      const closeA = Math.abs(c.x - d.x) < threshold;
      const closeB = Math.abs(c.y - d.y) < threshold;
      if (closeX && closeY) offsetY += 30;
      if (closeA && closeB) smOffsetY += 20;
    }
    clustered[rawPositions[i].id] = {
      x: rawPositions[i].x,
      y: rawPositions[i].y + offsetY,
    };
    clustered[rawPositionsSm[i].id] = {
      x: rawPositionsSm[i].x,
      y: rawPositionsSm[i].y + smOffsetY,
    };
  }
  return clustered;
};

export const shallowEqual = <T>(
  a: Record<string, T>,
  b: Record<string, T>,
): boolean => {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

export const shallowEqualArray = <T>(a: T[], b: T[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((item, i) => item === b[i]);
};

const getCombinedContainerText = (el: Node) => {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let text = "";
  let node: Node | null = null;

  while ((node = walker.nextNode())) {
    // Trim and add a space to prevent mashing
    if (node.nodeValue?.match(/^\n\s+$/)) {
      continue;
    }
    text += node.nodeValue;
  }
  return text.trim();
};

export const getRangeFromSnippet = (
  containerElement: HTMLElement,
  searchText: string,
) => {
  const containerCopy = containerElement.cloneNode(true) as HTMLElement;
  // to remove footnote references since they are not in the search text and can break the range calculation
  containerCopy.querySelectorAll("span.footnote").forEach((el) => {
    el.querySelectorAll("span.footnote-content").forEach((innerEl) => {
      // will always be an em tag
      el.replaceWith(
        innerEl.textContent?.replace(/^\s*\[\d+\]\s*/, "").trim() || "",
      );
    });
  });

  const text = getCombinedContainerText(containerCopy)
    // to normalize whitespace since the search text has normalized whitespace but the text content may have newlines and multiple spaces
    .replace(/\n/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const startOffset = text?.indexOf(searchText);
  if (startOffset === -1 || startOffset === undefined) return null;
  const range = document.createRange();
  let currentPos = 0;
  let startNode: Node | null = null;

  const walker = document.createTreeWalker(
    containerElement,
    NodeFilter.SHOW_TEXT,
  );
  let node: Node | null;

  while ((node = walker.nextNode())) {
    if (!node.textContent) continue;
    // to skip footnotes since they are not in the search text
    if (
      node.textContent.match(/^\s*\[\d+\]\s*$/) ||
      node.textContent.match(/^[\n\s]+$/)
    ) {
      continue;
    }
    let nodeLength = node.textContent.length;
    const footnoteSpacing = node.textContent.match(/\n\s+/g);
    if (footnoteSpacing) {
      // reduce the node length by the length of the spacing since it is not in the search text
      Array.from(footnoteSpacing).forEach((match) => {
        nodeLength -= match.length;
      });
    }

    if (!startNode && currentPos + nodeLength > startOffset) {
      range.setStart(node, startOffset - currentPos);
      startNode = node;
    }

    if (
      startNode &&
      currentPos + nodeLength >= startOffset + searchText.length
    ) {
      range.setEnd(node, startOffset + searchText.length - currentPos);
      break;
    }

    currentPos += nodeLength;
  }
  // if range end is the same as the start, return null since it means we failed to find the correct range
  if (
    range.startContainer === range.endContainer &&
    range.startOffset === range.endOffset
  ) {
    return null;
  }
  return range;
};

// Cleans a snippet by removing <em class="search-highlight"> tags
// and the ellipsis but keeping their content
export const cleanSnippet = (snippet: string): string => {
  const temp = document.createElement("div");
  temp.innerHTML = snippet || "";

  temp.querySelectorAll("em.search-highlight").forEach((el) => {
    el.replaceWith(...el.childNodes);
  });

  const cleanHtmlSnippet = temp.textContent;
  return (
    cleanHtmlSnippet
      ?.replace(/ …|… /g, "")
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .trim() || ""
  );
};

export type ContentsItemWithSearchHit = ContentsItem & {
  searchHit?: SearchHit;
  children?: ContentsItemWithSearchHit[];
};

const hasSearchHit = (item: ContentsItemWithSearchHit): boolean => {
  if (item.searchHit) {
    return true;
  }
  if (item.children) {
    return item.children.some((child) => hasSearchHit(child));
  }
  return false;
};

export const createSearchResultTree = (
  searchResults: SearchHit[],
  contents?: ContentsItem[],
): ContentsItemWithSearchHit[] => {
  if (!contents) return [];
  const resultTree: ContentsItemWithSearchHit[] = JSON.parse(
    JSON.stringify(contents),
  );

  searchResults.forEach((hit) => {
    const section: ContentsItemWithSearchHit | undefined =
      getSectionFromContents(resultTree, hit.objectID);
    if (section) {
      // this assumes that each hit corresponds to a unique section
      section.searchHit = hit;
    }
  });

  // add labels to all items on the tree in case they are missing
  const addLabels = (item: ContentsItemWithSearchHit) => {
    if (!item.label) {
      item.label = item.id;
    }
    if (item.children) {
      item.children.forEach(addLabels);
    }
  };
  resultTree.forEach(addLabels);

  const pruneTree = (item: ContentsItemWithSearchHit): boolean => {
    if (item.children) {
      item.children = item.children.filter(pruneTree);
    }
    return hasSearchHit(item);
  };

  return resultTree.filter(pruneTree);
};

export const isElementInViewport = (el: Element) => {
  const rect = el.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  // Consider the element "in viewport" if it is horizontally on screen
  // and its top (or a meaningful portion) is visible vertically.
  const horizontallyInView = rect.right >= 0 && rect.left <= viewportWidth;
  const topVisible = rect.top >= 0 && rect.top <= viewportHeight;
  const partiallyVisible = rect.top < 0 && rect.bottom > 0;
  return horizontallyInView && (topVisible || partiallyVisible);
};

const getAncestorIds = (id: string): string[] => {
  const parts = id.split("-");
  const base = parts[0];
  const hierarchy = parts.slice(1);

  const ancestors: string[] = [];
  for (let i = 1; i <= hierarchy.length; i++) {
    const ancestorId = [base, ...hierarchy.slice(0, i)].join("-");
    ancestors.push(ancestorId);
  }

  return ancestors.reverse();
};

export const scrollToElementOrAncestor = (id?: string) => {
  if (!id) return;
  // Check the element and its ancestors
  const ids = getAncestorIds(id);
  for (const ancestorId of ids) {
    const el = document.getElementById(ancestorId);
    if (el) {
      const visible = isElementInViewport(el);
      if (!visible) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      // If the element is already visible, no need to check further ancestors
      return;
    }
  }
  // If no element found, start decrementing the chapters
  if (ids.length < 3) return;
  const chapter = ids[ids.length - 3]; // assuming the id is like search-nav-<pubId>-ch:<chapterNumber>...
  if (!chapter) return;
  const chapterNumberString = chapter.match(/-ch:(\d+)$/)?.[1];
  const chapterNumber = chapterNumberString
    ? parseInt(chapterNumberString, 10)
    : NaN;
  if (isNaN(chapterNumber)) return;
  let currentChapter = chapterNumber - 1;
  while (currentChapter > 0) {
    const chapterId = chapter.replace(/-ch:\d+$/, `-ch:${currentChapter}`);
    const el = document.getElementById(chapterId);
    if (el) {
      const visible = isElementInViewport(el);
      if (!visible) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      // If the element is already visible, no need to check further chapters
      return;
    }
    currentChapter--;
  }
};
